import { t } from 'i18next';
import { isArray } from 'lodash';
import * as Yup from 'yup';
import { FormServFieldType } from '../../../../../shared/types';
import { COLUMNS_HIDE_ORDERING_KEY } from '../../../../../shared/constants';
import { getText } from '../../../../../shared/text';
import { MCR_DEFAULT_FIELDS } from '../../../../constants';
import {
  CUSTOM_CHOICE_LIMIT,
  LocalStorageItemKeyForFields,
  defaultField,
} from './constants';

const {
  settings: { customfields },
} = getText('EN');

export const getCustomFieldsFilter = (field: FormServFieldType) =>
  !field.field_options?.fs_default;

export const getBulkChoicesSchema = (): Yup.AnyObjectSchema => {
  return Yup.object({
    value: Yup.string()
      .required(`${t('forms.err_msg.required_field')}`)
      .test({
        message: 'Error',
        name: 'name',
        test: function (values, context) {
          const valueCount =
            values?.split('\n').filter((a: string) => a !== '') || [];
          if (valueCount?.length > CUSTOM_CHOICE_LIMIT) {
            return context.createError({
              message: `${t('forms.err_msg.max_limit', {
                limit: `${CUSTOM_CHOICE_LIMIT}`,
              })}`,
            });
          }
          return true;
        },
      }),
  });
};

export const getFormFieldSchema = ({
  fields,
  formValues,
}: {
  fields: FormServFieldType[];
  formValues: FormServFieldType;
}): Yup.AnyObjectSchema => {
  return Yup.object({
    id: Yup.string().required(customfields?.fields?.fieldId?.error),
    label: Yup.string()
      // .required('This is a required field')
      .trim()
      .max(
        50,
        `${t('forms.err_msg.max_char', {
          len: '50',
        })}`,
      )
      .min(
        1,
        `${t('forms.err_msg.min_char', {
          len: '1',
        })}`,
      )
      .test({
        message: 'Error',
        name: 'name',
        test: function (label, context) {
          const { createError } = context || {};
          const takenLabels = fields
            .filter((field) => field.id !== formValues.id)
            .map((field) => field.label);
          if (takenLabels.includes(label || '')) {
            return createError({
              message: `${t('forms.err_msg.unique_label')}`,
            });
          }
          return true;
        },
      }),
    name: Yup.string()
      .required(customfields?.fields?.fieldName?.error)
      .trim()
      .matches(
        /^[a-z0-9]([a-z_0-9])+[a-z0-9]+$/,
        `${t('forms.err_msg.validation')}`,
      )
      .max(
        50,
        `${t('forms.err_msg.max_char', {
          len: '50',
        })}`,
      )
      .min(
        3,
        `${t('forms.err_msg.min_char', {
          len: '3',
        })}`,
      )
      .test({
        message: 'Error',
        name: 'name',
        test: function (name, context) {
          const { createError } = context || {};
          if (
            MCR_DEFAULT_FIELDS.includes(name || '') &&
            !formValues.created_at
          ) {
            return createError({
              message: `${t('forms.err_msg.valid_field_name', {
                name: `${name}`,
              })}`,
            });
          }
          const takenNames = fields
            .filter((field) => field.id !== formValues.id)
            .map((field) => field.name);
          if (takenNames.includes(name || '')) {
            return createError({
              message: `${t('forms.err_msg.unique_name')}`,
            });
          }
          return true;
        },
      }),
    position: Yup.number(),
    type: Yup.number(),
    visible: Yup.boolean(),
    required: Yup.boolean(),
    editable: Yup.boolean(),
    deleted: Yup.boolean(),
    internal: Yup.boolean(),
    validatable: Yup.boolean(),
    derived: Yup.boolean(),
    placeholder: Yup.string(),
    hint: Yup.string(),
    fields: Yup.array().of(Yup.object()),
    choices: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required(`${t('forms.err_msg.required_field')}`),
          value: Yup.string()
            .trim()
            .required(`${t('forms.err_msg.required_field')}`)
            .max(
              50,
              `${t('forms.err_msg.max_char', {
                len: '50',
              })}`,
            )
            .min(
              3,
              `${t('forms.err_msg.min_char', {
                len: '3',
              })}`,
            ),
        }),
      )
      .test({
        message: 'Error',
        name: 'custom',
        test: function (values, context) {
          const uniqueCount = new Set(values?.map((choice) => choice.value));
          if (uniqueCount.size !== values?.length) {
            return context.createError({
              message: `${t('forms.err_msg.unique_options')}`,
            });
          }
          if (values?.length > CUSTOM_CHOICE_LIMIT) {
            return context.createError({
              message: `${t('forms.err_msg.max_limit', {
                limit: `${CUSTOM_CHOICE_LIMIT}`,
              })}`,
            });
          }
          return true;
        },
      }),
    account_id: Yup.number(),
    form_id: Yup.number(),
    column_type_blob: Yup.boolean(),
    nodegroup_skip: Yup.boolean(),
    field_class: Yup.string(),
    created_at: Yup.string(),
    updated_at: Yup.string(),
  }).required();
};

export const getFormFieldDefaultValues = (): FormServFieldType | undefined =>
  defaultField;

export const removeFieldFromHidden = (field: FormServFieldType) => {
  LocalStorageItemKeyForFields.forEach((key) => {
    const item = localStorage.getItem(key);
    const itemValue: string[] = item ? JSON.parse(item) : [];
    if (isArray(itemValue) && itemValue.length > 0) {
      localStorage.setItem(
        key,
        JSON.stringify(
          itemValue.filter(
            (hiddenFieldId) =>
              hiddenFieldId !== field[COLUMNS_HIDE_ORDERING_KEY],
          ),
        ),
      );
    }
  });
};

export const addNewFieldToHidden = (field: FormServFieldType) => {
  LocalStorageItemKeyForFields.forEach((key) => {
    const item = localStorage.getItem(key);
    const itemValue = item ? JSON.parse(item) : [];
    if (isArray(itemValue) && itemValue.length > 0) {
      localStorage.setItem(
        key,
        JSON.stringify([...itemValue, field[COLUMNS_HIDE_ORDERING_KEY]]),
      );
    }
  });
};

export const UUID_LENGTH_DD_CHOICES_FORMSERV = 13;
